import {useEffect, useState} from 'react';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';

const getLanguages = (countryCode, displayContentLanguages) =>
  COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];

export const buildDisplayContentFormData = (salesTarget, languages) =>
  Object.fromEntries(
    languages.map(({code}) => [
      code,
      {
        targetType: salesTarget?.targetType,
        ...salesTarget?.displayContent?.[code]
      }
    ])
  );

export const buildDisplayPreReqContentFormData = (salesTarget, languages) => {
  const parentData = {};
  const childData = {};

  languages.forEach(({code}) => {
    parentData[code] = {
      targetType: salesTarget?.targetType,
      ...salesTarget?.displayContent?.[code]
    };
    childData[code] = {
      targetType: salesTarget?.targetType,
      ...salesTarget?.child?.displayContent?.[code]
    };
  });

  return {...parentData, child: childData};
};

export const ensureDisplayContentLanguages = (
  existing = {},
  languages,
  targetType,
  includeCategories = false
) => {
  const updated = {...existing};
  languages.forEach(({code}) => {
    if (!updated[code]) {
      updated[code] = {
        targetType,
        title: '',
        excerpt: '',
        terms: '',
        category: '',
        ...(includeCategories && {parentCategory: '', childCategory: ''})
      };
    }
  });
  return updated;
};

export const useDisplayContentForm = (
  salesTarget,
  countryCode,
  displayContentLanguages
) => {
  const languages = getLanguages(countryCode, displayContentLanguages);
  const targetType = salesTarget?.targetType;
  const displayContent = salesTarget?.displayContent;
  const childDisplayContent = salesTarget?.child?.displayContent;
  const [displayContentFormData, setDisplayContentFormData] = useState(() => {
    return buildDisplayContentFormData(salesTarget, languages);
  });

  const [displayPreReqContentFormData, setDisplayPreReqContentFormData] =
    useState(() => {
      return buildDisplayPreReqContentFormData(salesTarget, languages);
    });

  useEffect(() => {
    if (languages.length > 0) {
      setDisplayContentFormData(
        buildDisplayContentFormData({targetType, displayContent}, languages)
      );
    }
  }, [
    countryCode,
    displayContentLanguages,
    languages,
    targetType,
    displayContent
  ]);

  useEffect(() => {
    if (languages.length > 0) {
      setDisplayPreReqContentFormData(
        buildDisplayPreReqContentFormData(
          {
            targetType,
            displayContent,
            child: {displayContent: childDisplayContent}
          },
          languages
        )
      );
    }
  }, [
    countryCode,
    displayContentLanguages,
    languages,
    targetType,
    displayContent,
    childDisplayContent
  ]);

  return {
    displayContentFormData,
    setDisplayContentFormData,
    displayPreReqContentFormData,
    setDisplayPreReqContentFormData
  };
};
